import {computed, onMounted, ref} from "vue"
import axios from "axios"
import {daysAgo, setQueryParam, toDateStr} from "@/common/utils/admin"

export interface ListBrowserOptions {
  /** Full URL of the object service, e.g. "/service/objects/stripe_webhook" */
  serviceUrl: string
  /** Default sort column when none selected. */
  defaultSortColumn: string
  /** Date column prefix for _ge / _le params, e.g. "date_created". */
  dateColumn: string
  /** Called during doSearch() to append screen-specific filter params to the API request. */
  buildFilterParams: (params: URLSearchParams) => void
  /**
   * Called on mount with the current URL's query params.
   * Use to populate screen-specific filter refs from bookmarked URLs.
   */
  readUrlParams?: (params: URLSearchParams) => void
  /**
   * Called during URL sync to add screen-specific params to the bookmarkable URL.
   * Separate from buildFilterParams so params like hide_unprocessed=0 can appear
   * in the URL without being sent to the API.
   */
  buildUrlParams?: (params: URLSearchParams) => void
}

export function useListBrowser(options: ListBrowserOptions) {
  // ── shared state ────────────────────────────────────────────────────────

  const dateFrom = ref("")
  const dateTo = ref("")
  const currentPage = ref(1)
  const perPage = ref(100)
  const sortBy = ref(options.defaultSortColumn)
  const sortDesc = ref(true)
  const totalRows = ref(0)
  const rows = ref<Array<Record<string, unknown> & { _showDetails?: boolean }>>([])

  const lastPage = computed(() => Math.max(1, Math.ceil(totalRows.value / perPage.value)))

  // ── URL bookmarking ──────────────────────────────────────────────────────

  function getApiParams(): URLSearchParams {
    const params = new URLSearchParams()
    if (currentPage.value > 1) setQueryParam(params, "page", currentPage.value)
    setQueryParam(params, "pageSize", perPage.value)
    setQueryParam(params, "sortBy", sortBy.value)
    setQueryParam(params, "sortDesc", sortDesc.value)

    options.buildFilterParams(params)

    if (dateFrom.value) {
      setQueryParam(params, `${options.dateColumn}_ge`, dateFrom.value + "T00:00:00")
    }
    if (dateTo.value) {
      setQueryParam(params, `${options.dateColumn}_le`, dateTo.value + "T23:59:59")
    }
    return params
  }

  /** Update the browser URL with current filter state for bookmarking. */
  function syncUrl(apiParams: URLSearchParams): void {
    const url = new URL(window.location.href)

    // Clear existing params, then rebuild from the API params (minus internal-only keys)
    const keep = new Set(["page", "pageSize", "sortBy", "sortDesc"])
    for (const key of apiParams.keys()) {
      if (!keep.has(key) && !key.endsWith("_ge") && !key.endsWith("_le")) {
        const val = apiParams.get(key)!
        if (val) url.searchParams.set(key, val)
      }
    }

    // Date params: use dateFrom/dateTo as URL-friendly names
    if (dateFrom.value) url.searchParams.set("dateFrom", dateFrom.value)
    else url.searchParams.delete("dateFrom")
    if (dateTo.value) url.searchParams.set("dateTo", dateTo.value)
    else url.searchParams.delete("dateTo")

    // Sort: only include if non-default
    if (sortBy.value !== options.defaultSortColumn) url.searchParams.set("sortBy", sortBy.value)
    else url.searchParams.delete("sortBy")
    if (!sortDesc.value) url.searchParams.set("sortDesc", "false")
    else url.searchParams.delete("sortDesc")

    // Page size / page: only include if non-default
    if (currentPage.value > 1) url.searchParams.set("page", String(currentPage.value))
    else url.searchParams.delete("page")
    if (perPage.value !== 100) url.searchParams.set("pageSize", String(perPage.value))
    else url.searchParams.delete("pageSize")

    // Screen-specific URL-only params (e.g. hide_unprocessed=0)
    if (options.buildUrlParams) {
      options.buildUrlParams(url.searchParams)
    }

    window.history.replaceState({}, "", url.toString())
  }

  /** Read filter state from URL params on mount. */
  function applyUrlParams(): void {
    const urlParams = new URLSearchParams(window.location.search)

    if (urlParams.has("dateFrom")) dateFrom.value = urlParams.get("dateFrom")!
    if (urlParams.has("dateTo")) dateTo.value = urlParams.get("dateTo")!
    if (urlParams.has("page")) currentPage.value = parseInt(urlParams.get("page")!, 10) || 1
    if (urlParams.has("pageSize")) perPage.value = parseInt(urlParams.get("pageSize")!, 10) || 100
    if (urlParams.has("sortBy")) sortBy.value = urlParams.get("sortBy")!
    if (urlParams.has("sortDesc")) sortDesc.value = urlParams.get("sortDesc") !== "false"

    // Let the screen read its own filter params
    options.readUrlParams?.(urlParams)
  }

  // ── shared methods ───────────────────────────────────────────────────────

  function doSearch(): void {
    const params = getApiParams()
    syncUrl(params)

    axios.get(`${options.serviceUrl}?${params.toString()}`).then(resp => {
      const data = resp.data.data || resp.data
      totalRows.value = data.total || 0
      rows.value = (data.data || []).map((item: Record<string, unknown>) => ({...item, _showDetails: false}))
    })
  }

  function onSortChanged(ctx: {sortBy: string; sortDesc: boolean}): void {
    sortBy.value = ctx.sortBy
    sortDesc.value = ctx.sortDesc
    currentPage.value = 1
    doSearch()
  }

  function setDateRange(days: number): void {
    if (days === 0) {
      dateFrom.value = ""
      dateTo.value = ""
    } else {
      dateFrom.value = toDateStr(daysAgo(days))
      dateTo.value = ""
    }
    doSearch()
  }

  function dateRangeActive(label: string): boolean {
    if (label === "all") return dateFrom.value === "" && dateTo.value === ""
    const days = parseInt(label)
    const expected = toDateStr(daysAgo(days))
    return dateFrom.value === expected && dateTo.value === ""
  }

  // ── lifecycle ────────────────────────────────────────────────────────────

  onMounted(() => {
    applyUrlParams()
    doSearch()
  })

  return {
    dateFrom,
    dateTo,
    currentPage,
    perPage,
    sortBy,
    sortDesc,
    totalRows,
    rows,
    lastPage,
    doSearch,
    onSortChanged,
    setDateRange,
    dateRangeActive,
  }
}
